import AccountPermissions from "@saleor/components/AccountPermissions";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { PermissionGroupErrorFragment } from "@saleor/fragments/types/PermissionGroupErrorFragment";
import { Coupons_SubPermission_Enums } from "@saleor/permissionGroups/utils/utils";
import { PermissionEnum } from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getPermissionGroupErrorMessage from "@saleor/utils/errors/permissionGroups";
import React from "react";
import { useIntl } from "react-intl";

import CouponsSubPermissionGroupDialog from "../CouponsSubPermissionGroupDialog";
import { PermissionData } from "../PermissionGroupDetailsPage";
import PermissionGroupInfo from "../PermissionGroupInfo";

export interface PermissionGroupCreatePageFormData {
  name: string;
  hasFullAccess: boolean;
  isActive: boolean;
  permissions: PermissionEnum[];
}

const initialForm: PermissionGroupCreatePageFormData = {
  hasFullAccess: false,
  isActive: false,
  name: "",
  permissions: []
};

export interface PermissionGroupCreatePageProps {
  disabled: boolean;
  errors: PermissionGroupErrorFragment[];
  permissions: PermissionData[];
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  onSubmit(data: PermissionGroupCreatePageFormData);
}

const PermissionGroupCreatePage: React.FC<PermissionGroupCreatePageProps> = ({
  disabled,
  permissions,
  onBack,
  onSubmit,
  saveButtonBarState,
  errors
}) => {
  const intl = useIntl();
  const formErrors = getFormErrors(["addPermissions"], errors || []);
  const couponSubPermissionArray: string[] = Object.values(
    Coupons_SubPermission_Enums
  );
  let initialCouponSubAccessContent = "None";
  const permissionsError = getPermissionGroupErrorMessage(
    formErrors.addPermissions,
    intl
  );

  const breadcrumbDirectory = [
    {
      name: "User Types",
      url: "/permission-groups"
    },
    {
      name: "Create User Types"
    }
  ];

  return (
    <Form initial={initialForm} onSubmit={onSubmit} confirmLeave>
      {({ data, change, submit, hasChanged }) => {
        const userCanManageCoupon = data?.permissions?.includes(
          PermissionEnum.MANAGE_COUPONS
        );
        const currentUserPermission: string[] = data?.permissions;
        const userHasCouponPermission = couponSubPermissionArray.some(value =>
          currentUserPermission.includes(value)
        );

        if (userCanManageCoupon && userHasCouponPermission === false) {
          initialCouponSubAccessContent = "AllAccess";
        } else if (!userCanManageCoupon && userHasCouponPermission === true) {
          initialCouponSubAccessContent = "None";
        }

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader
                title={intl.formatMessage({
                  defaultMessage: "Create User Type"
                })}
              ></PageHeader>
            </div>
            <Grid>
              <div>
                <PermissionGroupInfo
                  data={data}
                  errors={errors}
                  onChange={change}
                  disabled={disabled}
                />
                <FormSpacer />
                {userCanManageCoupon && (
                  <CouponsSubPermissionGroupDialog
                    data={data}
                    onChange={change}
                  />
                )}
              </div>
              <div>
                <AccountPermissions
                  permissionsExceeded={false}
                  data={data}
                  errorMessage={permissionsError}
                  disabled={disabled}
                  permissions={permissions}
                  initialCouponSubAccessContent={initialCouponSubAccessContent}
                  onChange={change}
                  fullAccessLabel={intl.formatMessage({
                    defaultMessage: "Group has full access to the store",
                    description: "checkbox label"
                  })}
                  description={intl.formatMessage({
                    defaultMessage:
                      "Expand or restrict group's permissions to access certain part of saleor system.",
                    description: "card description"
                  })}
                />
              </div>
            </Grid>
            <div>
              <SaveButtonBar
                onCancel={onBack}
                onSave={submit}
                state={saveButtonBarState}
                disabled={disabled || !hasChanged}
              />
            </div>
          </Container>
        );
      }}
    </Form>
  );
};
PermissionGroupCreatePage.displayName = "PermissionGroupCreatePage";
export default PermissionGroupCreatePage;
